import { HttpInterceptorFn, HttpErrorResponse, HttpRequest, HttpEvent, HttpHandlerFn, HttpContextToken } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, map, switchMap, throwError } from 'rxjs';
import { LoginService } from '../../auth/login/login.service';
import { buildClientRoute } from '../utils/client-route.util';
import { isPublicAuthPath } from '../constants/auth-routes.constants';
import { SnackbarService } from '../../../@backoffice/services/snackbar.service';

// Per-request flag stored in HttpContext — client-side only, never sent over the
// wire (so it cannot trigger CORS preflight). Marks a request that has already
// been retried after a token refresh; a second 401 with this flag set means
// the refreshed token still doesn't work, so hard-logout.
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

/** API error code for a campaign wizard step the role may not change (CampaignsController). */
const STEP_PERMISSION_REQUIRED = 'STEP_PERMISSION_REQUIRED';

const isRefreshEndpointUrl =(url: string): boolean => /\/Auth\/Refresh/i.test(url);

const cloneWithToken = (req: HttpRequest<unknown>, token: string): HttpRequest<unknown> => {
  return req.clone({
    setHeaders: { 'Authorization': `Bearer ${token}` },
    context: req.context.set(RETRIED_AFTER_REFRESH, true)
  });
};

const navigateToLoginIfProtected = (loginService: LoginService, router: Router, currentUrl: string): void => {
  if (isPublicAuthPath(currentUrl)) return;

  if (currentUrl && currentUrl !== '/' &&
      !currentUrl.includes('/forgot-password') &&
      !currentUrl.includes('/reset-password')) {
    loginService.setRedirectUrl(currentUrl);
  }

  router.navigate(buildClientRoute(router, ['/login'])).catch((navError) => {
    console.error('Failed to navigate to login page:', navError);
  });
};

const failHard = (
  error: unknown,
  loginService: LoginService,
  router: Router,
  currentUrl: string
): Observable<never> => {
  console.warn('Auth refresh failed; logging out.', error);
  loginService.logout();
  navigateToLoginIfProtected(loginService, router, currentUrl);
  return throwError(() => error);
};

const getOrStartRefresh = (loginService: LoginService): Observable<string> =>
  loginService.refreshAccessTokenShared().pipe(map((response) => response.accessToken));

const handle401 = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  loginService: LoginService,
  router: Router,
  originalError: HttpErrorResponse
): Observable<HttpEvent<unknown>> => {
  const currentUrl = router.url;

  // 401 on the refresh endpoint itself means the refresh token is dead.
  // Don't try to refresh-the-refresh — just logout.
  if (isRefreshEndpointUrl(req.url)) {
    return failHard(originalError, loginService, router, currentUrl);
  }

  // Already retried after a refresh — refusing to loop. Hard logout.
  if (req.context.get(RETRIED_AFTER_REFRESH)) {
    return failHard(originalError, loginService, router, currentUrl);
  }

  // No Authorization header on the request → nothing to refresh.
  // Covers public endpoints (login/forgot-password/etc.) and pre-login state.
  if (!req.headers.has('Authorization')) {
    return throwError(() => originalError);
  }

  // On a public auth page, 401 means bad credentials, not stale token. Propagate.
  if (isPublicAuthPath(currentUrl)) {
    return throwError(() => originalError);
  }

  // No refresh token available → can't refresh. Hard logout.
  if (!loginService.getRefreshToken()) {
    return failHard(originalError, loginService, router, currentUrl);
  }

  // Coalesced refresh, then retry the failed request with the new token.
  return getOrStartRefresh(loginService).pipe(
    catchError((refreshError) => failHard(refreshError, loginService, router, currentUrl)),
    switchMap((newToken) => next(cloneWithToken(req, newToken)).pipe(
      catchError((retryError) => {
        if (retryError instanceof HttpErrorResponse && retryError.status === 401) {
          // Refreshed token still rejected — something is structurally wrong. Logout.
          return failHard(retryError, loginService, router, currentUrl);
        }
        return throwError(() => retryError);
      })
    ))
  );
};

/**
 * HTTP Interceptor: attaches the Bearer token, handles 401 via single-flight
 * token refresh + retry, and redirects 403 to the access-denied page.
 *
 * 401 flow:
 *   - First 401 on a protected request → call /Auth/Refresh, retry once with new token.
 *   - Concurrent 401s share the same refresh call (module-scope coalescing).
 *   - 401 on /Auth/Refresh itself, on retried requests, or with no refresh token → logout.
 *   - 401 on public endpoints or while on a public auth page → propagate (bad creds, not session).
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const loginService = inject(LoginService);
  const router = inject(Router);
  const snackbar = inject(SnackbarService);

  const authToken = loginService.getAuthToken();

  const modifiedReq = authToken
    ? req.clone({ setHeaders: { 'Authorization': `Bearer ${authToken}` } })
    : req;

  return next(modifiedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return handle401(modifiedReq, next, loginService, router, error);
      }

      if (error.status === 403) {
        // A campaign step the role cannot change: stay in the wizard so unsaved work is kept,
        // and show the API's translated reason instead of leaving the page.
        if (error.error?.errorCode === STEP_PERMISSION_REQUIRED) {
          snackbar.error(error.error?.message || 'Your role cannot change this part of the campaign.');
          return throwError(() => error);
        }

        const currentUrl = router.url;
        if (!currentUrl.includes('/access-denied')) {
          console.warn(`Forbidden access detected (403). Redirecting to access denied page.`);
          router.navigate(buildClientRoute(router, ['/access-denied'])).catch((navError) => {
            console.error('Failed to navigate to access denied page:', navError);
          });
        }
      }

      return throwError(() => error);
    })
  );
};
